import { BillOfMaterialsStatus } from "@redotech/crdb-enums/inventory/bill-of-materials-status";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import { FlowInProgress } from "@redotech/redo-flows-common/flow-in-progress";
import { OmsConditionType } from "@redotech/redo-flows-core-common/oms-condition-type";
import {
  WorkOrderFlowTriggerConditions,
  WorkOrderTriggerGroupNode,
  WorkOrderTriggerNode,
  WorkOrderTriggerSource,
} from "@redotech/redo-fulfillment-operations-common/work-order-flow/work-order-flow";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoInventoryRpcClientContext } from "@redotech/redo-merchant-app-common/inventory-rpc-client-provider";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonTabGroup } from "@redotech/redo-web/arbiter-components/checkbox/redo-button-tab-group";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  BadgeRedoListItem,
  RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DeepPartial } from "..";
import { ConditionFields } from "./work-order-condition";

interface BomOption {
  id: string;
  name: string;
}

function BomPicker({
  triggerBomIds,
  setTriggerBomIds,
}: {
  triggerBomIds: string[];
  setTriggerBomIds: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  const inventoryRpcClient = useContext(RedoInventoryRpcClientContext);

  const bomLoad = useLoad(async () => {
    if (!inventoryRpcClient) {
      return [];
    }
    const result = await inventoryRpcClient.getBillOfMaterialsList({
      status: BillOfMaterialsStatus.Active,
    });
    const seen = new Set<string>();
    const deduped: BomOption[] = [];
    for (const item of result.items) {
      const id = String(item.id);
      if (!seen.has(id)) {
        seen.add(id);
        deduped.push({ id, name: item.name });
      }
    }
    return deduped;
  }, [inventoryRpcClient]);

  const bomOptions = useMemo(() => bomLoad.value ?? [], [bomLoad.value]);
  const loading = bomLoad.pending;

  const options: BadgeRedoListItem<string>[] = useMemo(
    () =>
      bomOptions.map((bom) => ({
        id: bom.id,
        type: "badge" as const,
        badge: { text: bom.name },
        value: bom.id,
      })),
    [bomOptions],
  );

  const selectedOptions: BadgeRedoListItem<string>[] = useMemo(
    () =>
      triggerBomIds.flatMap((id) => {
        const bom = bomOptions.find((b) => b.id === id);
        if (!bom) {
          return [];
        }
        return [
          {
            id: bom.id,
            type: "badge" as const,
            badge: { text: bom.name },
            value: bom.id,
          } satisfies BadgeRedoListItem<string>,
        ];
      }),
    [triggerBomIds, bomOptions],
  );

  const handleSetSelectedOptions = useHandler(
    (items: BadgeRedoListItem<string>[]) => {
      setTriggerBomIds(items.map((item) => item.value));
    },
  );

  return (
    <RedoTagsDropdownInput
      fitToAnchor
      label={t("Bills of materials")}
      loading={loading}
      options={options}
      placeholder={t("Select BOMs...")}
      selectedOptions={selectedOptions}
      setSelectedOptions={handleSetSelectedOptions}
      size="xs"
    />
  );
}

function BomTagPicker({
  triggerBomTags,
  setTriggerBomTags,
}: {
  triggerBomTags: string[];
  setTriggerBomTags: (tags: string[]) => void;
}) {
  const { t } = useTranslation();
  const inventoryRpcClient = useContext(RedoInventoryRpcClientContext);
  const [inputValue, setInputValue] = useState("");

  const bomLoad = useLoad(async () => {
    if (!inventoryRpcClient) {
      return [];
    }
    const result = await inventoryRpcClient.getBillOfMaterialsList({});
    const tagSet = new Set<string>();
    for (const item of result.items) {
      for (const tag of item.tags ?? []) {
        tagSet.add(tag);
      }
    }
    return [...tagSet].sort();
  }, [inventoryRpcClient]);

  const allTags = useMemo(() => bomLoad.value ?? [], [bomLoad.value]);
  const loading = bomLoad.pending;

  const options: BadgeRedoListItem<string>[] = useMemo(
    () =>
      allTags.map((tag) => ({
        id: tag,
        type: "badge" as const,
        badge: { text: tag },
        value: tag,
      })),
    [allTags],
  );

  const selectedOptions: BadgeRedoListItem<string>[] = useMemo(
    () =>
      triggerBomTags.map((tag) => ({
        id: tag,
        type: "badge" as const,
        badge: { text: tag },
        value: tag,
      })),
    [triggerBomTags],
  );

  const handleSetSelectedOptions = useHandler(
    (items: BadgeRedoListItem<string>[]) => {
      setTriggerBomTags(items.map((item) => item.value));
    },
  );

  const handleAddTag = useHandler(() => {
    const tag = inputValue.trim();
    if (tag && !triggerBomTags.includes(tag)) {
      setTriggerBomTags([...triggerBomTags, tag]);
    }
    setInputValue("");
  });

  return (
    <Flex dir="column" gap="sm" w="full">
      <RedoTagsDropdownInput
        fitToAnchor
        label={t("BOM tags")}
        loading={loading}
        options={options}
        placeholder={t("Select BOM tags...")}
        selectedOptions={selectedOptions}
        setSelectedOptions={handleSetSelectedOptions}
        size="xs"
      />
      <Flex align="flex-end" gap="xs" w="full">
        <Flex flex="1">
          <RedoTextInput
            placeholder={t("Add custom tag...")}
            setValue={setInputValue}
            size="sm"
            value={inputValue}
          />
        </Flex>
        {inputValue.trim() && (
          <Text
            fontSize="xs"
            fontWeight="medium"
            onClick={handleAddTag}
            style={{ cursor: "pointer", color: "var(--redo-colors-text-link)" }}
          >
            {t("Add")}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}

function ConditionGroup({
  group,
  setGroup,
  onRemove,
}: {
  group: WorkOrderTriggerGroupNode;
  setGroup: (group: WorkOrderTriggerNode) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const toggleConjunction = useCallback(() => {
    setGroup({
      ...group,
      conjunctionMode:
        group.conjunctionMode === ConjunctionMode.AND
          ? ConjunctionMode.OR
          : ConjunctionMode.AND,
    });
  }, [group, setGroup]);

  const updateChild = useCallback(
    (newChild: WorkOrderTriggerNode, index: number) => {
      setGroup({
        ...group,
        children: group.children.map((c, i) => (i === index ? newChild : c)),
      });
    },
    [group, setGroup],
  );

  const removeChild = useCallback(
    (index: number) => {
      const updated = group.children.filter((_, i) => i !== index);
      if (updated.length === 0) {
        onRemove();
      } else {
        setGroup({ ...group, children: updated });
      }
    },
    [group, setGroup, onRemove],
  );

  const addLeaf = useCallback(() => {
    setGroup({
      ...group,
      children: [
        ...group.children,
        { nodeType: "leaf" } as unknown as WorkOrderTriggerNode,
      ],
    });
  }, [group, setGroup]);

  return (
    <Flex
      borderColor="primary"
      borderLeftWidth="2px"
      borderStyle="solid"
      dir="column"
      gap="sm"
      pl="lg"
      w="full"
    >
      <Flex align="center" gap="sm" justify="space-between">
        <Text
          fontSize="xs"
          fontWeight="medium"
          onClick={toggleConjunction}
          style={{ cursor: "pointer", userSelect: "none" }}
          textColor="brand-primary"
        >
          Group (
          {group.conjunctionMode === ConjunctionMode.AND
            ? t("All match")
            : t("Any match")}
          )
        </Text>
        <RedoButton
          hierarchy="tertiary"
          onClick={onRemove}
          size="xs"
          text={t("Remove group")}
        />
      </Flex>
      {group.children.map((child, index) => (
        <Flex dir="column" gap="sm" key={index} w="full">
          {index > 0 && (
            <Text
              fontSize="xs"
              fontWeight="medium"
              onClick={toggleConjunction}
              style={{ cursor: "pointer", userSelect: "none" }}
              textColor="brand-primary"
            >
              {group.conjunctionMode === ConjunctionMode.AND
                ? t("And")
                : t("Or")}
            </Text>
          )}
          {child.nodeType === "leaf" ? (
            <Flex align="flex-start" dir="row" gap="sm" w="full">
              <Flex dir="row" flex="1" gap="md" wrap="wrap">
                <ConditionFields
                  index={index}
                  setConditionType={(
                    item: RedoListItem<OmsConditionType>,
                    idx: number,
                  ) => {
                    updateChild(
                      { ...child, conditionType: item.value } as any,
                      idx,
                    );
                  }}
                  setStep={(
                    newLeaf: DeepPartial<{ conditionType: OmsConditionType }>,
                    idx: number,
                  ) => {
                    updateChild(
                      {
                        ...child,
                        ...newLeaf,
                        nodeType: "leaf" as const,
                      } as any,
                      idx,
                    );
                  }}
                  step={child as any}
                />
              </Flex>
              <RedoButton
                hierarchy="tertiary"
                onClick={() => removeChild(index)}
                size="xs"
                text={t("Remove")}
              />
            </Flex>
          ) : (
            <ConditionGroup
              group={child}
              onRemove={() => removeChild(index)}
              setGroup={(newGroup) => updateChild(newGroup, index)}
            />
          )}
        </Flex>
      ))}
      <RedoButton
        hierarchy="secondary"
        onClick={addLeaf}
        size="xs"
        text={t("Add condition")}
      />
    </Flex>
  );
}

function ProductTriggerConditions({
  triggerConditions,
  setTriggerConditions,
}: {
  triggerConditions: WorkOrderFlowTriggerConditions | undefined;
  setTriggerConditions: (value: WorkOrderFlowTriggerConditions) => void;
}) {
  const { t } = useTranslation();
  const children: WorkOrderTriggerNode[] = useMemo(
    () => triggerConditions?.children ?? [],
    [triggerConditions],
  );

  const conjunctionMode =
    triggerConditions?.conjunctionMode ?? ConjunctionMode.AND;

  const updateChildren = useCallback(
    (newChildren: WorkOrderTriggerNode[]) => {
      setTriggerConditions({ conjunctionMode, children: newChildren });
    },
    [conjunctionMode, setTriggerConditions],
  );

  const toggleConjunction = useCallback(() => {
    setTriggerConditions({
      conjunctionMode:
        conjunctionMode === ConjunctionMode.AND
          ? ConjunctionMode.OR
          : ConjunctionMode.AND,
      children,
    });
  }, [conjunctionMode, children, setTriggerConditions]);

  const updateChild = useCallback(
    (newChild: WorkOrderTriggerNode, index: number) => {
      const updated = children.map((c, i) => (i === index ? newChild : c));
      updateChildren(updated);
    },
    [children, updateChildren],
  );

  const removeChild = useCallback(
    (index: number) => {
      updateChildren(children.filter((_, i) => i !== index));
    },
    [children, updateChildren],
  );

  const addLeaf = useCallback(() => {
    updateChildren([
      ...children,
      { nodeType: "leaf" } as unknown as WorkOrderTriggerNode,
    ]);
  }, [children, updateChildren]);

  const addGroup = useCallback(() => {
    updateChildren([
      ...children,
      {
        nodeType: "group",
        conjunctionMode: ConjunctionMode.AND,
        children: [{ nodeType: "leaf" } as unknown as WorkOrderTriggerNode],
      },
    ]);
  }, [children, updateChildren]);

  return (
    <Flex dir="column" gap="md" w="full">
      {children.map((child, index) => (
        <Flex dir="column" gap="sm" key={index} w="full">
          {index === 0 ? (
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {t("Where")}
            </Text>
          ) : (
            <Text
              fontSize="xs"
              fontWeight="medium"
              onClick={toggleConjunction}
              style={{ cursor: "pointer", userSelect: "none" }}
              textColor="brand-primary"
            >
              {conjunctionMode === ConjunctionMode.AND ? t("And") : t("Or")}
            </Text>
          )}
          {child.nodeType === "leaf" ? (
            <Flex align="flex-start" dir="row" gap="sm" w="full">
              <Flex dir="column" flex="1" gap="sm">
                <Flex dir="row" gap="md" w="full" wrap="wrap">
                  <ConditionFields
                    index={index}
                    setConditionType={(
                      item: RedoListItem<OmsConditionType>,
                      idx: number,
                    ) => {
                      updateChild(
                        { ...child, conditionType: item.value } as any,
                        idx,
                      );
                    }}
                    setStep={(
                      newLeaf: DeepPartial<{ conditionType: OmsConditionType }>,
                      idx: number,
                    ) => {
                      updateChild(
                        {
                          ...child,
                          ...newLeaf,
                          nodeType: "leaf" as const,
                        } as any,
                        idx,
                      );
                    }}
                    step={child as any}
                  />
                </Flex>
              </Flex>
              <RedoButton
                hierarchy="tertiary"
                onClick={() => removeChild(index)}
                size="xs"
                text={t("Remove")}
              />
            </Flex>
          ) : (
            <ConditionGroup
              group={child}
              onRemove={() => removeChild(index)}
              setGroup={(newGroup) => updateChild(newGroup, index)}
            />
          )}
        </Flex>
      ))}
      <Flex gap="sm">
        <RedoButton
          hierarchy="secondary"
          onClick={addLeaf}
          size="xs"
          text={t("Add condition")}
        />
        <RedoButton
          hierarchy="tertiary"
          onClick={addGroup}
          size="xs"
          text={t("Add group")}
        />
      </Flex>
    </Flex>
  );
}

export const WorkOrderTriggerInspector = memo(
  function WorkOrderTriggerInspector({
    automation,
    setTriggerConditions: _setTriggerConditions,
    setAutomation,
  }: {
    automation?: FlowInProgress | null;
    triggerConditions: WorkOrderFlowTriggerConditions | undefined;
    setTriggerConditions: (value: WorkOrderFlowTriggerConditions) => void;
    setAutomation?: React.Dispatch<React.SetStateAction<FlowInProgress | null>>;
  }) {
    const { t } = useTranslation();
    // Default to PRODUCT for new flows
    const [triggerSource, setTriggerSourceState] =
      useState<WorkOrderTriggerSource>(WorkOrderTriggerSource.PRODUCT);
    const [triggerBomIds, setTriggerBomIdsState] = useState<string[]>([]);
    const [triggerBomTags, setTriggerBomTagsState] = useState<string[]>([]);

    // Sync from automation on mount
    useEffect(() => {
      if (automation) {
        setTriggerSourceState(
          automation.triggerSource ?? WorkOrderTriggerSource.PRODUCT,
        );
        setTriggerBomIdsState(automation.triggerBomIds ?? []);
        setTriggerBomTagsState(automation.triggerBomTags ?? []);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setTriggerSource = useHandler((source: WorkOrderTriggerSource) => {
      setTriggerSourceState(source);
      setAutomation?.((prev) =>
        prev ? { ...prev, triggerSource: source } : prev,
      );
    });

    const setTriggerBomIds = useHandler((ids: string[]) => {
      setTriggerBomIdsState(ids);
      setAutomation?.((prev) =>
        prev ? { ...prev, triggerBomIds: ids } : prev,
      );
    });

    const setTriggerBomTags = useHandler((tags: string[]) => {
      setTriggerBomTagsState(tags);
      setAutomation?.((prev) =>
        prev ? { ...prev, triggerBomTags: tags } : prev,
      );
    });

    const isBom = triggerSource === WorkOrderTriggerSource.BOM;
    const isBomTag = triggerSource === WorkOrderTriggerSource.BOM_TAG;
    const isProduct = triggerSource === WorkOrderTriggerSource.PRODUCT;

    function getStatusText() {
      if (isBom) {
        return triggerBomIds.length === 0
          ? t("No BOMs selected — this flow will not trigger.")
          : t("Triggers for {{count}} selected BOMs.", {
              count: triggerBomIds.length,
            });
      }
      if (isBomTag) {
        return triggerBomTags.length === 0
          ? t("No BOM tags selected — this flow will not trigger.")
          : t("Triggers for BOMs matching {{count}} tags.", {
              count: triggerBomTags.length,
            });
      }
      if (isProduct) {
        return t("Triggers based on product conditions set in the flow.");
      }
      return "";
    }

    return (
      <Flex dir="column" gap="md" w="full">
        <Flex dir="column" gap="xs" w="full">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Trigger source")}
          </Text>
          <RedoButtonTabGroup
            onChange={(source) =>
              setTriggerSource(source as WorkOrderTriggerSource)
            }
            options={[
              {
                value: WorkOrderTriggerSource.PRODUCT,
                el: <Text fontSize="sm">{t("Product")}</Text>,
              },
              {
                value: WorkOrderTriggerSource.BOM,
                el: <Text fontSize="sm">{t("Bill of materials")}</Text>,
              },
              {
                value: WorkOrderTriggerSource.BOM_TAG,
                el: <Text fontSize="sm">{t("BOM tags")}</Text>,
              },
            ]}
            value={triggerSource}
          />
          <Text
            fontSize="xs"
            fontWeight="regular"
            style={{ textAlign: "center" }}
            textColor="secondary"
          >
            {getStatusText()}
          </Text>
        </Flex>

        <Flex mt="3xl" w="full">
          {isBom ? (
            <BomPicker
              setTriggerBomIds={setTriggerBomIds}
              triggerBomIds={triggerBomIds}
            />
          ) : isBomTag ? (
            <BomTagPicker
              setTriggerBomTags={setTriggerBomTags}
              triggerBomTags={triggerBomTags}
            />
          ) : isProduct ? (
            <ProductTriggerConditions
              setTriggerConditions={_setTriggerConditions}
              triggerConditions={automation?.triggerConditions}
            />
          ) : null}
        </Flex>
      </Flex>
    );
  },
);
